import axios from 'axios';

const envBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
const isProd = process.env.NODE_ENV === 'production';
const isLocalUrl = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(envBackendUrl);

const BACKEND_URL =
  isProd
    ? (envBackendUrl && !isLocalUrl ? envBackendUrl : window.location.origin)
    : (envBackendUrl || 'http://localhost:8000');

const API = `${BACKEND_URL}/api/portfolio`;

const getAlternateProjectApis = () => {
  const candidates = [];
  const sameOriginApi = `${window.location.origin}/api/portfolio`;

  if (API !== sameOriginApi) {
    candidates.push(sameOriginApi);
  }

  if (envBackendUrl && !isLocalUrl) {
    const envApi = `${envBackendUrl}/api/portfolio`;
    if (envApi !== API && envApi !== sameOriginApi) {
      candidates.push(envApi);
    }
  }

  return candidates;
};

class PortfolioAPI {
  constructor() {
    this.axiosInstance = axios.create({
      timeout: 10000, // 10 second timeout
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Add request interceptor for debugging
    this.axiosInstance.interceptors.request.use(
      (config) => {
        console.log(`Making API request to: ${config.url}`);
        return config;
      },
      (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for debugging
    this.axiosInstance.interceptors.response.use(
      (response) => {
        console.log(`API response from ${response.config.url}:`, response.status);
        return response;
      },
      (error) => {
        console.error(`API error from ${error.config?.url}:`, error.message);
        return Promise.reject(error);
      }
    );
  }

  // Contact Form
  async submitContactForm(formData) {
    try {
      const response = await this.axiosInstance.post(`${API}/contact`, formData);
      return response.data;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  }

  // Personal Information
  async getPersonalInfo() {
    try {
      const response = await this.axiosInstance.get(`${API}/personal-info`);
      return response.data;
    } catch (error) {
      console.error('Error fetching personal info:', error);
      throw error;
    }
  }

  // Skills
  async getSkills() {
    try {
      const response = await this.axiosInstance.get(`${API}/skills`);
      return response.data;
    } catch (error) {
      console.error('Error fetching skills:', error);
      throw error;
    }
  }

  // Projects
  async getProjects() {
    const projectApis = [API, ...getAlternateProjectApis()];

    try {
      for (const baseApi of projectApis) {
        try {
          const response = await this.axiosInstance.get(`${baseApi}/projects`, {
            params: { _t: Date.now() }
          });

          if (Array.isArray(response.data)) {
            return response.data;
          }
        } catch (innerError) {
          console.warn(`Projects fetch failed at ${baseApi}:`, innerError?.message || innerError);
        }
      }

      throw new Error('All configured project API endpoints failed');
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  // Certifications
  async getCertifications() {
    try {
      const response = await this.axiosInstance.get(`${API}/certifications`);
      return response.data;
    } catch (error) {
      console.error('Error fetching certifications:', error);
      throw error;
    }
  }

  // Analytics
  async trackVisit(page = '/') {
    try {
      await this.axiosInstance.post(`${API}/analytics/visit`, { page });
    } catch (error) {
      console.error('Error tracking visit:', error);
      // Don't throw error for analytics - it's not critical
    }
  }

  // Portfolio Stats
  async getPortfolioStats() {
    try {
      const response = await this.axiosInstance.get(`${API}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio stats:', error);
      throw error;
    }
  }
}

export default new PortfolioAPI();